/**
 * Core types and URL normalization for inline citations.
 *
 * Kept free of React/DOM so it can be unit tested in isolation (see
 * citation-source.spec.ts).
 */

import type { ResourceType } from '@cotera/api';

/** A citable source harvested from a tool result. */
export type CitationSource =
  | {
      kind: 'web';
      /** The URL exactly as the tool reported it — used as the chip's href. */
      url: string;
      /** Page title when the tool provided one. */
      title?: string;
      /** Host without `www.`, e.g. `ycombinator.com`. */
      siteLabel: string;
    }
  | {
      kind: 'resource';
      /** In-app relative path, e.g. `/acme/documents/<uuid>`. */
      url: string;
      title?: string;
      /** Rendered in place of a favicon site label. */
      siteLabel: string;
      /** Folders have no citable page of their own. */
      resourceType: Exclude<ResourceType, ResourceType.Folder>;
      resourceId: string;
    };

/** Query params that identify a campaign, not a document. */
const TRACKING_PARAM_PREFIXES = ['utm_'] as const;
const TRACKING_PARAMS = new Set([
  'gclid',
  'fbclid',
  'mc_cid',
  'mc_eid',
  'ref',
  'ref_src',
]);

function isTrackingParam(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    TRACKING_PARAMS.has(lower) ||
    TRACKING_PARAM_PREFIXES.some((prefix) => lower.startsWith(prefix))
  );
}

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Registry key for an in-app resource citation. */
export function resourceCitationKey(resourceId: string): string {
  return `resource:${resourceId.toLowerCase()}`;
}

/**
 * Stable lookup key for a citation URL.
 *
 * The model reliably paraphrases URLs it copies out of tool results — dropping
 * `www.`, flipping the scheme, adding or removing a trailing slash, reordering
 * query params. Registry keys and rendered hrefs are normalized through here so
 * those variations still resolve; the source's original string is what actually
 * gets rendered as the href.
 *
 * Relative in-app paths key on the resource UUID alone
 * ({@link resourceCitationKey}). The surrounding path carries the org slug and
 * a route segment, both of which the model gets wrong often enough to matter
 * and neither of which identifies the resource.
 *
 * Returns undefined when the value isn't a usable URL.
 */
export function normalizeCitationUrl(
  rawUrl: string | null | undefined
): string | undefined {
  if (typeof rawUrl !== 'string') {
    return undefined;
  }
  const trimmed = rawUrl.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  // Relative in-app path — identify by the resource UUID it contains.
  if (trimmed.startsWith('/')) {
    const match = UUID_PATTERN.exec(trimmed);
    return match === null ? undefined : resourceCitationKey(match[0]);
  }

  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return undefined;
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const path = stripTrailingSlash(url.pathname);
  const query = normalizeQuery(url.search.replace(/^\?/, ''));

  // Scheme and hash are deliberately dropped: http/https variants of the same
  // page are the same source, and a fragment never changes which page it is.
  return query === '' ? `${host}${path}` : `${host}${path}?${query}`;
}

/** `/a/b/` → `/a/b`, but a bare `/` is preserved. */
function stripTrailingSlash(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/** Drops tracking params and sorts the rest so param order doesn't matter. */
function normalizeQuery(query: string | undefined): string {
  if (query === undefined || query === '') {
    return '';
  }
  const params = new URLSearchParams(query);
  const kept: [string, string][] = [];
  for (const [key, value] of params.entries()) {
    if (!isTrackingParam(key)) {
      kept.push([key, value]);
    }
  }
  kept.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return kept.map(([key, value]) => `${key}=${value}`).join('&');
}
